import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import Icon from '../../docs/Icon'
import Toast, { type ToastMessage } from '../ui/Toast'
import Popover from '../ui/Popover'

// Live examples for toast, progress bar, spinner, skeleton and popover. A mini
// specimen is a still preview for the catalog: no ids, nothing focusable.

type SpecimenProps = { mini?: boolean }

function Still({ children }: { children: ReactNode }) {
  return <div className="lib-framed lib-still">{children}</div>
}

const toastMessages: Omit<ToastMessage, 'id'>[] = [
  { message: 'Draft saved.', tone: 'success' },
  { message: 'Your address was updated.', tone: 'success' },
  { message: 'A new version of this form is available.', tone: 'info' },
]

function ToastSpecimen({ mini }: SpecimenProps) {
  const [toasts, setToasts] = useState<ToastMessage[]>([])
  const count = useRef(0)
  const dismiss = useCallback((id: string) => setToasts((list) => list.filter((toast) => toast.id !== id)), [])
  if (mini)
    return (
      <Still>
        <span className="mds-toast mds-toast--success lib-toast-still">
          <Icon name="check" size={18} className="mds-toast__icon" />
          <span className="mds-toast__message">Draft saved.</span>
          <span className="mds-toast__close lib-toast-still__close">
            <Icon name="close" size={16} />
          </span>
        </span>
      </Still>
    )
  const show = () => {
    const next = toastMessages[count.current % toastMessages.length]
    count.current += 1
    setToasts((list) => [...list, { ...next, id: `toast-${count.current}` }])
  }
  return (
    <div className="mds-cluster">
      <button type="button" className="mds-button mds-button--primary" onClick={show}>
        Save draft
      </button>
      <Toast toasts={toasts} onDismiss={dismiss} />
    </div>
  )
}

function ProgressBar({ label, value, mini }: { label: string; value?: number; mini?: boolean }) {
  const id = useId()
  const indeterminate = value === undefined
  return (
    <div className={`mds-progress${indeterminate ? ' mds-progress--indeterminate' : ''}`}>
      <div className="mds-progress__head">
        <span className="mds-progress__label" id={mini ? undefined : id}>
          {label}
        </span>
        <span className="mds-progress__value" aria-hidden="true">
          {indeterminate ? 'Working' : `${value}%`}
        </span>
      </div>
      <div
        className="mds-progress__track"
        role={mini ? undefined : 'progressbar'}
        aria-labelledby={mini ? undefined : id}
        aria-valuemin={mini || indeterminate ? undefined : 0}
        aria-valuemax={mini || indeterminate ? undefined : 100}
        aria-valuenow={mini || indeterminate ? undefined : value}
      >
        <span className="mds-progress__bar" style={indeterminate ? undefined : { width: `${value}%` }} />
      </div>
    </div>
  )
}

function ProgressBarSpecimen({ mini }: SpecimenProps) {
  const [value, setValue] = useState(0)
  const [running, setRunning] = useState(false)
  const active = running && value < 100

  useEffect(() => {
    if (!active) return
    const timer = window.setInterval(() => setValue((current) => Math.min(100, current + 5)), 300)
    return () => window.clearInterval(timer)
  }, [active])

  if (mini)
    return (
      <Still>
        <ProgressBar label="Uploading documents" value={45} mini />
      </Still>
    )
  return (
    <div className="lib-stack">
      <ProgressBar label="Uploading documents" value={value} />
      <div className="mds-cluster">
        <button
          type="button"
          className="mds-button mds-button--secondary mds-button--sm"
          onClick={() => {
            setValue(0)
            setRunning(true)
          }}
        >
          {value === 100 ? 'Upload again' : 'Start upload'}
        </button>
      </div>
      <ProgressBar label="Checking your application" />
    </div>
  )
}

function SpinnerSpecimen({ mini }: SpecimenProps) {
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!loading) return
    const timer = window.setTimeout(() => setLoading(false), 2000)
    return () => window.clearTimeout(timer)
  }, [loading])

  if (mini)
    return (
      <Still>
        <span className="mds-cluster">
          <span className="mds-spinner" aria-hidden="true" />
          <span className="mds-spinner mds-spinner--sm" aria-hidden="true" />
        </span>
      </Still>
    )
  return (
    <div className="lib-stack">
      <div className="mds-cluster">
        <span className="mds-spinner" role="status">
          <span className="mds-visually-hidden">Loading</span>
        </span>
        <span className="mds-spinner mds-spinner--sm" role="status">
          <span className="mds-visually-hidden">Loading</span>
        </span>
      </div>
      <div className="mds-cluster">
        <button type="button" className="mds-button mds-button--secondary" onClick={() => setLoading(true)}>
          Find offices
        </button>
        <span role="status" className="mds-cluster">
          {loading ? (
            <>
              <span className="mds-spinner mds-spinner--sm" aria-hidden="true" />
              <span>Finding offices</span>
            </>
          ) : null}
        </span>
      </div>
    </div>
  )
}

function SkeletonBlocks({ mini }: { mini?: boolean }) {
  return (
    <div className="mds-skeleton__blocks" aria-hidden="true">
      <span className="mds-skeleton__media" />
      <span className="mds-skeleton__heading" />
      <span className="mds-skeleton__line" />
      {!mini && <span className="mds-skeleton__line" />}
      <span className="mds-skeleton__line mds-skeleton__line--short" />
    </div>
  )
}

function SkeletonSpecimen({ mini }: SpecimenProps) {
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!loading) return
    const timer = window.setTimeout(() => setLoading(false), 2500)
    return () => window.clearTimeout(timer)
  }, [loading])

  if (mini)
    return (
      <Still>
        <div className="mds-skeleton lib-skeleton-demo">
          <SkeletonBlocks mini />
        </div>
      </Still>
    )
  return (
    <div className="lib-stack lib-skeleton-demo">
      <div className="mds-skeleton" aria-busy={loading}>
        {loading ? (
          <>
            <span className="mds-visually-hidden">Loading office details</span>
            <SkeletonBlocks />
          </>
        ) : (
          <article className="lib-skeleton-loaded">
            <span className="lib-skeleton-loaded__media" aria-hidden="true">
              <Icon name="landmark" size={32} />
            </span>
            <h3 className="mds-h3">Riverside field office</h3>
            <p>Open Monday to Friday, 8 a.m. to 4:30 p.m. Walk-ins are seen until 3 p.m.</p>
            <p className="mds-muted">Average wait: 25 minutes</p>
          </article>
        )}
      </div>
      <div className="mds-cluster">
        <button type="button" className="mds-button mds-button--secondary mds-button--sm" onClick={() => setLoading(true)}>
          Reload
        </button>
      </div>
    </div>
  )
}

function PopoverSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span className="mds-button mds-button--secondary mds-button--sm lib-popover-still__button">Processing times</span>
        <span className="mds-popover__panel lib-popover-still">
          <strong className="mds-popover__title">Processing times</strong>
          <span className="mds-popover__body">Most applications finish in six weeks.</span>
        </span>
      </Still>
    )
  return (
    <div className="lib-popover-demo">
      <Popover label="Processing times" title="Processing times">
        <p>Most applications finish in six to eight weeks. Expedited service takes two to three weeks for an added fee.</p>
        <p>
          <a className="mds-link" href="#processing-times">
            See current processing times
          </a>
        </p>
      </Popover>
    </div>
  )
}

export const feedbackSpecimens: Record<string, (props: SpecimenProps) => ReactNode> = {
  toast: ToastSpecimen,
  'progress-bar': ProgressBarSpecimen,
  spinner: SpinnerSpecimen,
  skeleton: SkeletonSpecimen,
  popover: PopoverSpecimen,
}
